import apiFetch from "@/app/auth/apiFetch";
import { NavigateFunction } from "react-router-dom";

const API_URL = import.meta.env.VITE_API_URL;

export interface QueueItem {
  item: unknown;
  score: number;
}

export interface QueueDetails {
  key: string;
  total: number;
  pending_count?: number;
  processing_count?: number;
  items: QueueItem[];
  start: number;
  limit: number;
}

export interface QueueSummarySection {
  key: string;
  count: number;
  processing_count?: number;
  total_count?: number;
  top: QueueItem[];
  processing_top?: QueueItem[];
}

export interface QueueSummaryResponse {
  invite: QueueSummarySection;
  message: QueueSummarySection;
  enrichment: QueueSummarySection;
  search: QueueSummarySection;
  company_enrichment: QueueSummarySection;
  school_enrichment: QueueSummarySection;
  company_jobs: QueueSummarySection;
}

export interface CreateEnrichmentTaskRequest {
  candidate_id: number;
  profile_url: string;
  campaign_candidate_id?: number;
}

export interface CreateSearchTaskRequest {
  search_type: "profiles" | "schools" | "companies";
  query: string;
  filters?: Record<string, unknown>;
  max_results?: number;
  callback_url?: string;
  campaign_id?: number;
}

export interface CreateTaskResponse {
  status: string;
  task_id?: string;
  added: number;
}

export async function getQueuesSummary(navigate, params?: { top?: number; subscription_id?: number }) {
  const q = new URLSearchParams();
  if (params?.top) q.set("top", String(params.top));
  if (params?.subscription_id) q.set("subscription_id", String(params.subscription_id));
  const url = `${API_URL}/worker/queues/summary${q.toString() ? `?${q.toString()}` : ""}`;
  const res = await apiFetch(url, { method: "GET" }, navigate);
  if (!res?.ok) throw new Error("Erreur lors du chargement du résumé des files");
  return res.json() as Promise<QueueSummaryResponse>;
}

export async function getQueueDetails(
  navigate,
  type: "invite" | "message" | "enrichment" | "search" | "company_enrichment" | "school_enrichment" | "company_jobs",
  params?: { start?: number; limit?: number; subscription_id?: number }
) {
  const q = new URLSearchParams();
  q.set("type", type);
  if (params?.start !== undefined) q.set("start", String(params.start));
  if (params?.limit !== undefined) q.set("limit", String(params.limit));
  if (params?.subscription_id) q.set("subscription_id", String(params.subscription_id));
  const url = `${API_URL}/worker/queues/details?${q.toString()}`;
  const res = await apiFetch(url, { method: "GET" }, navigate);
  if (!res?.ok) throw new Error("Erreur lors du chargement du détail de la file");
  return res.json() as Promise<QueueDetails>;
}

export async function deleteQueueItem(
  navigate,
  data: {
    type: "invite" | "message" | "enrichment" | "search" | "company_enrichment" | "school_enrichment" | "company_jobs";
    item: unknown;
    subscription_id?: number;
    key?: string;
    subscription_filter?: number;
  }
) {
  const url = `${API_URL}/worker/queues/item`;
  const res = await apiFetch(url, {
    method: "DELETE",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(data),
  }, navigate);
  if (!res?.ok) throw new Error("Erreur lors de la suppression de l'élément");
  return res.json() as Promise<{ removed: number }>;
}

export async function flushQueue(
  navigate,
  type: "invite" | "message" | "enrichment" | "search" | "company_enrichment" | "school_enrichment" | "company_jobs"
) {
  const url = `${API_URL}/worker/queues/flush`;
  const res = await apiFetch(url, {
    method: "DELETE",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ type }),
  }, navigate);
  if (!res?.ok) throw new Error("Erreur lors du vidage de la file");
  return res.json() as Promise<{ removed: number }>;
}

export async function createCompanyEnrichmentTask(
  navigate,
  data: { company_id: number; company_external_ref: string }
) {
  const url = `${API_URL}/worker/tasks/company-enrichment`;
  const res = await apiFetch(url, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(data),
  }, navigate);
  if (!res?.ok) throw new Error("Erreur lors de la creation de la tache");
  return res.json() as Promise<CreateTaskResponse>;
}

export async function createSchoolEnrichmentTask(
  navigate,
  data: { school_id: number; school_external_ref: string; school_profile_url?: string }
) {
  const url = `${API_URL}/worker/tasks/school-enrichment`;
  const res = await apiFetch(url, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(data),
  }, navigate);
  if (!res?.ok) throw new Error("Erreur lors de la creation de la tache");
  return res.json() as Promise<CreateTaskResponse>;
}

export async function createEnrichmentTask(
  navigate,
  data: CreateEnrichmentTaskRequest
) {
  const url = `${API_URL}/worker/tasks/enrichment`;
  const res = await apiFetch(url, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(data),
  }, navigate);
  if (!res?.ok) throw new Error("Erreur lors de la création de la tâche d'enrichissement");
  return res.json() as Promise<CreateTaskResponse>;
}

export async function createSearchTask(
  navigate,
  data: CreateSearchTaskRequest
) {
  const url = `${API_URL}/worker/tasks/search`;
  const res = await apiFetch(url, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(data),
  }, navigate);
  if (!res?.ok) throw new Error("Erreur lors de la création de la tâche de recherche");
  return res.json() as Promise<CreateTaskResponse>;
}

// ---------------- Libellés & ordre des files (cockpit) ----------------

export type QueueKey =
  | "invite"
  | "message"
  | "enrichment"
  | "search"
  | "company_enrichment"
  | "school_enrichment"
  | "company_jobs";

// Ordre d'affichage : files d'enrichissement d'abord (enjeu famine), puis
// recherche, puis actions de campagne par compte.
export const QUEUE_ORDER: QueueKey[] = [
  "enrichment",
  "company_enrichment",
  "school_enrichment",
  "company_jobs",
  "search",
  "invite",
  "message",
];

export const QUEUE_LABELS: Record<QueueKey, string> = {
  invite: "Invitations",
  message: "Messages",
  enrichment: "Enrich. candidats",
  search: "Recherche",
  company_enrichment: "Enrich. entreprises",
  school_enrichment: "Enrich. écoles",
  company_jobs: "Offres d'emploi",
};

// Files servies au pool d'enrichissement (globales, sans identité utilisateur).
// Ce sont celles qui ont des verrous processing_*:{worker_id} et un historique
// de profondeur — donc les seules pertinentes pour l'in-flight par worker.
export type PoolQueueKey =
  | "enrichment"
  | "search"
  | "company_enrichment"
  | "school_enrichment"
  | "company_jobs";

export const POOL_QUEUE_ORDER: PoolQueueKey[] = [
  "enrichment",
  "company_enrichment",
  "school_enrichment",
  "company_jobs",
  "search",
];

// ---------------- Tier 2 : in-flight (tâches en cours) par worker ----------------

export interface InflightWorker {
  worker_id: string;
  counts: Record<PoolQueueKey, number>;
  total: number;
  /** Deadline la plus proche (epoch s) parmi les tâches détenues. < now => lock orphelin. */
  nearest_deadline: number | null;
}

export interface InflightByWorkerResponse {
  workers: InflightWorker[];
  generated_at: number; // epoch s
}

export async function getInflightByWorker(navigate: NavigateFunction): Promise<InflightByWorkerResponse> {
  const url = `${API_URL}/worker/queues/inflight-by-worker`;
  const res = await apiFetch(url, { method: "GET" }, navigate);
  if (!res?.ok) throw new Error("Erreur lors du chargement des tâches en cours par worker");
  return res.json() as Promise<InflightByWorkerResponse>;
}

// ---------------- Tier 2 : historique de profondeur des files ----------------

export interface QueueHistoryPoint {
  ts: number; // epoch s
  enrichment: number;
  search: number;
  company_enrichment: number;
  school_enrichment: number;
  company_jobs: number;
}

export interface QueueHistoryResponse {
  window_seconds: number;
  points: QueueHistoryPoint[];
}

export async function getQueueHistory(
  navigate: NavigateFunction,
  params?: { windowSeconds?: number },
): Promise<QueueHistoryResponse> {
  const q = new URLSearchParams();
  if (params?.windowSeconds) q.set("window", String(params.windowSeconds));
  const url = `${API_URL}/worker/queues/history${q.toString() ? `?${q}` : ""}`;
  const res = await apiFetch(url, { method: "GET" }, navigate);
  if (!res?.ok) throw new Error("Erreur lors du chargement de l'historique des files");
  return res.json() as Promise<QueueHistoryResponse>;
}

// ---------------- Tâches écartées (dead-letter) : suivi + rejeu/abandon -------

export type DeadLetterQueueKey =
  | "enrichment"
  | "company_enrichment"
  | "school_enrichment"
  | "company_jobs";

export interface DeadLetterItem {
  queue: DeadLetterQueueKey;
  member: string; // brut — requis pour rejouer/abandonner
  reason: string | null;
  dead_lettered_at: string | null;
  entity_id: number | null;
  age_seconds: number | null;
  payload: Record<string, unknown>;
}

export interface DeadLettersResponse {
  items: DeadLetterItem[];
  totals: Record<string, number>;
}

export async function getDeadLetters(
  navigate: NavigateFunction,
  params?: { queue?: DeadLetterQueueKey; limit?: number; offset?: number },
): Promise<DeadLettersResponse> {
  const q = new URLSearchParams();
  if (params?.queue) q.set("queue", params.queue);
  if (params?.limit !== undefined) q.set("limit", String(params.limit));
  if (params?.offset !== undefined) q.set("offset", String(params.offset));
  const url = `${API_URL}/worker/queues/dead-letters${q.toString() ? `?${q}` : ""}`;
  const res = await apiFetch(url, { method: "GET" }, navigate);
  if (!res?.ok) throw new Error("Erreur lors du chargement des tâches écartées");
  return res.json() as Promise<DeadLettersResponse>;
}

export async function replayDeadLetter(
  navigate: NavigateFunction,
  data: { queue: DeadLetterQueueKey; member: string },
): Promise<{ replayed: boolean; requeued?: boolean; entity_id?: number | null; reason?: string }> {
  const url = `${API_URL}/worker/queues/dead-letters/replay`;
  const res = await apiFetch(
    url,
    { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify(data) },
    navigate,
  );
  if (!res?.ok) throw new Error("Erreur lors du rejeu de la tâche écartée");
  return res.json();
}

export async function discardDeadLetter(
  navigate: NavigateFunction,
  data: { queue: DeadLetterQueueKey; member: string },
): Promise<{ discarded: boolean }> {
  const url = `${API_URL}/worker/queues/dead-letters/discard`;
  const res = await apiFetch(
    url,
    { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify(data) },
    navigate,
  );
  if (!res?.ok) throw new Error("Erreur lors de l'abandon de la tâche écartée");
  return res.json();
}

// --- Import de recherche LinkedIn distribué (pages réparties + résilient + suivi) ---

export interface CreateSearchImportRequest {
  search_type: "profiles" | "schools" | "companies";
  query: string;
  filters?: Record<string, unknown>;
  max_results?: number; // borné à 1000 côté serveur (plafond LinkedIn)
  campaign_id?: number;
}

export interface SearchImportChunkInfo {
  chunk_index: number;
  start_offset: number;
  status: string;
  candidates_imported: number;
  candidates_skipped: number;
  error_count: number;
  total: number | null;
}

export interface SearchImportJob {
  id: number;
  status: string;
  search_type: string;
  query: string;
  campaign_id: number | null;
  requested_target: number;
  real_total: number | null;
  chunks_total: number;
  chunks_done: number;
  chunks_failed: number;
  candidates_imported: number;
  candidates_skipped: number;
  error_count: number;
  cancel_requested: boolean;
  error_message: string | null;
  created_by: string | null;
  created_at: string | null;
  started_at: string | null;
  finished_at: string | null;
  chunks: SearchImportChunkInfo[];
}

export async function createSearchImport(navigate, data: CreateSearchImportRequest) {
  const url = `${API_URL}/worker/search/import`;
  const res = await apiFetch(url, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(data),
  }, navigate);
  if (!res?.ok) throw new Error("Erreur lors de la création de l'import de recherche");
  return res.json() as Promise<{ job_id: number; status: string }>;
}

export async function getSearchImport(navigate, jobId: number) {
  const url = `${API_URL}/worker/search/import/${jobId}`;
  const res = await apiFetch(url, { method: "GET" }, navigate);
  if (!res?.ok) throw new Error("Erreur lors du chargement de l'import");
  return res.json() as Promise<SearchImportJob>;
}

export async function cancelSearchImport(navigate, jobId: number) {
  const url = `${API_URL}/worker/search/import/${jobId}/cancel`;
  const res = await apiFetch(url, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
  }, navigate);
  if (!res?.ok) throw new Error("Erreur lors de l'annulation de l'import");
  return res.json() as Promise<{ status: string }>;
}
